
import { Component, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import Tabs from "@/components/Tabs/Tabs.vue";
import { TTab } from "@/models/tabs";
import { SMApp, SMCases, SMDocuments, SMUser } from "@/store";
import { EMPLOYER_ROLE } from "@/constants/roles";
import { APPROVED_SERVICE_STATUS, TO_REVIEW_SERVICE_STATUS } from "@/constants/services";
import {
  ERROR_DOCUMENT_STATUS,
  NOT_PROVIDED_DOCUMENT_STATUS,
  ADDITIONAL_DOCUMENT_TYPE,
} from "@/constants/documents";
import { RDocumentCard } from "@/components/DocumentViewCards/views/Card/RDocumentCard";
import { RDocument } from "@/components/DocumentViewCards/RDocument";

const EXPERT_COMPANIES_PATH = "/expert/companies";

const ROUTE_LABEL_DOCUMENT = "documents";
const ROUTE_LABEL_SUMMARY = "summary";

const COMPANY_LEVEL = "company";
const COMPANY_EMPLOYEE_LEVEL = "company_employee";

@Component({
  components: {
    Tabs,
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    ManageSubscriptionPopup: () => import("@/components/Popups/ManageSubscriptionPopup.vue"),
    ManageServicesPopup: () => import("@/components/Popups/ManageServicesPopup.vue"),
    CreateTaskPopUp: () => import("@/components/Popups/CreateTaskPopUp.vue"),
  },
})
export default class CompaniesTemplate extends Vue {
  get isTabOpened() {
    return !this.$route.params.tabId;
  }

  get textButton() {
    return this.documentIsApproved
      ? this.$tc("labels.document.withdrawApproval")
      : this.$tc("labels.document.approveDocument");
  }

  get isActionShown() {
    return this.$route.meta?.label !== ROUTE_LABEL_DOCUMENT;
  }

  get isExpertDocumentTemplate() {
    return SMUser.isExpert && !this.isActionShown;
  }

  get validShowPage() {
    const routeName = this.$route.name;

    if (!routeName) return false;

    return [RDocument.name, RDocumentCard.name].includes(routeName);
  }

  get showExpertDocumentTemplate() {
    return this.isExpertDocumentTemplate && this.validShowPage && !this.documentIsAdditionalType;
  }

  get documentIsApproved() {
    return SMDocuments.documentCard?.expert_status === APPROVED_SERVICE_STATUS;
  }

  get documentIsAdditionalType() {
    return SMDocuments.documentCard?.type === ADDITIONAL_DOCUMENT_TYPE;
  }

  get isDocumentApproveDisabled() {
    return (
      SMDocuments.documentCard?.status === NOT_PROVIDED_DOCUMENT_STATUS ||
      SMDocuments.documentCard?.status === ERROR_DOCUMENT_STATUS
    );
  }

  async expertUpdateDocumentStatus() {
    const { company_id, document_id, id } = this.$route.params;

    const status = this.documentIsApproved ? TO_REVIEW_SERVICE_STATUS : APPROVED_SERVICE_STATUS;

    if (!document_id) return;

    if (this.isCompanyEmployeeLevel) {
      await this.axios.get(
        API_ROUTES.EXPERT_COMPLIANCE_COMPANY_EMPLOYEE_STATUS(company_id, id, document_id, status)
      );
    } else {
      await this.axios.get(
        API_ROUTES.EXPERT_COMPLIANCE_COMPANY_DOCUMENT_STATUS(company_id, document_id, status)
      );
    }

    await this.initDocument();
  }

  async initDocument() {
    const { document_id, id } = this.$route.params;

    SMDocuments.setDocumentId(document_id);
    SMDocuments.setCaseId(Number(id ?? 0));

    const data = await SMDocuments.fetchDocumentCard();

    if (!data) return;

    SMDocuments.setDocumentCard(data);
    SMDocuments.initChecks();
    SMDocuments.initSelectedChecks();
  }

  get level() {
    return this.$route.path.includes("/employees/") ? COMPANY_EMPLOYEE_LEVEL : COMPANY_LEVEL;
  }

  get isCompanyEmployeeLevel() {
    return this.level === COMPANY_EMPLOYEE_LEVEL;
  }

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  get userId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get tabs(): TTab[] {
    const { companyId: id } = this;

    const path = `/expert/companies/${id}`;

    const expertTabs: TTab[] = [
      {
        name: this.$root.$tc("modules.names.summary"),
        path: `${path}/summary`,
        label: this.$root.$tc("modules.names.summary").toUpperCase(),
        isActive: this.$route.path.includes("summary"),
      },
      {
        name: this.$root.$tc("modules.names.companyDetails"),
        path: `${path}/company-details`,
        label: this.$root.$tc("modules.names.companyDetails").toUpperCase(),
        isActive: this.$route.path.includes("company-details"),
      },
      {
        name: this.$root.$tc("modules.names.sponsorLicence"),
        path: `${path}/sponsor-licence`,
        label: this.$root.$tc("modules.names.sponsorLicence").toUpperCase(),
        isActive: this.$route.path.includes("sponsor-licence"),
      },
      {
        name: this.$root.$tc("modules.names.documents"),
        path: `${path}/documents`,
        label: this.$root.$tc("modules.names.documents").toUpperCase(),
        isActive: this.$route.path.includes("documents"),
      },
      {
        name: this.$root.$tc("modules.names.employees"),
        path: `${path}/employees`,
        label: this.$root.$tc("modules.names.employees").toUpperCase(),
        isActive: this.$route.path.includes("employees"),
      },
      {
        name: this.$root.$tc("modules.names.services"),
        path: `${path}/services`,
        label: this.$root.$tc("modules.names.services").toUpperCase(),
        isActive: this.$route.path.includes("services"),
      },
      {
        name: this.$root.$tc("modules.names.tasks"),
        path: `${path}/tasks`,
        label: this.$root.$tc("modules.names.tasks").toUpperCase(),
        isActive: this.$route.path.includes("tasks"),
      },
      {
        name: this.$root.$tc("modules.names.dataHistory"),
        path: `${path}/data-history`,
        label: this.$root.$tc("modules.names.dataHistory").toUpperCase(),
        isActive: this.$route.path.includes("data-history"),
      },
    ];

    return expertTabs;
  }

  tabIsEditable = false;

  tabAlias? = "";

  selectTab(tab: TTab): void {
    this.tabAlias = tab?.alias;
  }

  get isEmployeesTab() {
    return this.$route.fullPath.includes("employees");
  }

  init = false;

  activated() {
    if (this.companyName) this.setPageTitle(this.companyName);
  }

  get companyName() {
    return SMApp.companyName;
  }

  @Watch("companyName", { immediate: true })
  companyNameChanged(name: string) {
    if (!name) this.fetchCompanyName();
  }

  async fetchCompanyName() {
    if (!this.companyId) return;

    try {
      const { data } = await this.axios.get<string>(
        API_ROUTES.EXPERT_COMPANIES_NAME(this.companyId)
      );

      if (!data) return;

      await this.setPageTitle(data);

      SMApp.setCompanyName(data);
    } finally {
      this.init = true;
    }
  }

  async setPageTitle(title: string) {
    await this.$nextTick();

    const name = `${this.$root.$tc("modules.names.companies")}`;

    SMApp.setPageTitle(name);

    const sectionName = ` > ${this.companyId} ${title}`;

    SMApp.setPageSection(sectionName);

    SMApp.setPageTitleLink(EXPERT_COMPANIES_PATH);
  }

  get isSummaryTab() {
    return this.$route.meta?.label === ROUTE_LABEL_SUMMARY;
  }

  get isExpert() {
    return SMUser.isExpert;
  }

  routerKey = 0;

  get showManageSubscription() {
    return this.isExpert && this.isSummaryTab && !this.isEmployeesTab;
  }

  manageSubscriptionPopup = false;

  openManageSubscriptionPopup() {
    this.manageSubscriptionPopup = true;
  }

  closeManageSubscriptionPopup() {
    this.manageSubscriptionPopup = false;

    this.routerKey++;
  }

  get showManageServices() {
    return this.isExpert && this.isSummaryTab && !this.isEmployeesTab;
  }

  manageServicesPopup = false;

  openManageServicesPopup() {
    this.manageServicesPopup = true;
  }

  closeManageServicesPopup() {
    this.manageServicesPopup = false;

    this.routerKey++;
  }

  roleTypeForCreatingTask = EMPLOYER_ROLE;

  createTaskPopup = false;

  get initCreateTaskPopup() {
    return this.isExpert;
  }

  openCreateTaskPopup() {
    this.createTaskPopup = true;

    const target = this.$refs.createTaskPopup as Vue & { open: () => {} };

    if (!target) return;

    target.open();
  }

  closeCreateTaskPopup() {
    this.createTaskPopup = false;
  }

  atUpdate() {
    SMCases.setWidgetNeedsUpdate(true);

    if (this.level === COMPANY_LEVEL) this.$root.$emit("update-company-level-data");

    if (this.level === COMPANY_EMPLOYEE_LEVEL) this.$root.$emit("update-employee-level-data");
  }
}
